// src/services/http.js
import { apiBase, DEFAULT_TIMEOUT, STORAGE_KEYS } from '../config/apiConfig'

// Build deep-object query strings, joining arrays with commas:
// { page:1, filters:{ level1: ['A','B'], ext:['jpg'] } }
// -> ?page=1&filters[level1]=A,B&filters[ext]=jpg
export function buildQuery(params = {}) {
  const usp = new URLSearchParams()

  const add = (key, value) => {
    if (value == null) return
    if (Array.isArray(value)) {
      if (value.length === 0) return
      usp.append(key, value.join(',')) // join arrays with commas
    } else if (typeof value === 'object') {
      Object.entries(value).forEach(([k, v]) => add(`${key}[${k}]`, v))
    } else {
      usp.append(key, String(value))
    }
  }

  Object.entries(params).forEach(([k, v]) => add(k, v))
  return usp.toString()
}

async function withTimeout(promise, ms = DEFAULT_TIMEOUT) {
  const ctrl = new AbortController()
  const id = setTimeout(() => ctrl.abort('timeout'), ms)
  try {
    return await promise(ctrl.signal)
  } finally {
    clearTimeout(id)
  }
}

async function doFetch(method, pathOrUrl, { params, headers, body } = {}) {
  const url = pathOrUrl.startsWith('http')
    ? pathOrUrl
    : apiBase(pathOrUrl)

  const qs = params ? buildQuery(params) : ''
  const finalUrl = qs ? `${url}?${qs}` : url

  const res = await withTimeout(async (signal) => {
    return fetch(finalUrl, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(headers || {}),
      },
      body: body ? JSON.stringify(body) : undefined,
      signal,
    })
  })

  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`
    try {
      const data = await res.json()
      if (data?.error) msg = data.error
    } catch { /* ignore */ }
    const err = new Error(msg)
    err.status = res.status
    throw err
  }

  // No content
  if (res.status === 204) return null

  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('application/json')) return res.json()
  return res.text()
}

export const http = {
  get: (path, opts) => doFetch('GET', path, opts),
  post: (path, opts) => doFetch('POST', path, opts),
}

// Attach X-API-Key automatically for researcher calls
export const authedHttp = {
  get: (path, opts = {}) => {
    const key = localStorage.getItem(STORAGE_KEYS.researcherKey) || ''
    return http.get(path, {
      ...opts,
      headers: { ...(opts.headers || {}), 'X-API-Key': key },
    })
  },
  post: (path, opts = {}) => {
    const key = localStorage.getItem(STORAGE_KEYS.researcherKey) || ''
    return http.post(path, {
      ...opts,
      headers: { ...(opts.headers || {}), 'X-API-Key': key },
    })
  },
}

// Separate admin client (X-Admin-Key / X-Admin-Secret)
export const adminHttp = {
  post: (path, { adminId, adminSecret, ...rest } = {}) =>
    http.post(path, {
      ...rest,
      headers: {
        ...(rest?.headers || {}),
        'X-Admin-Key': adminId,
        'X-Admin-Secret': adminSecret,
      },
    }),
}
